import SearchIcon from "@mui/icons-material/Search";
import {
  Avatar,
  Box,
  Button,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { useState } from "react";

const isPetPhotoUrl = (photoUrl) => {
  if (!photoUrl || typeof photoUrl !== "string") {
    return false;
  }

  return photoUrl.includes("/storage/pets/");
};

const getAssignedQr = (pet) => pet?.qr_code ?? pet?.qr_codes?.[0] ?? null;

export default function PetList({
  currentPage,
  currentPets,
  expandedPetId,
  filteredPets,
  itemsPerPage,
  onDelete,
  onEdit,
  onPageChange,
  onQrCodeFilterChange,
  onSearchChange,
  onToggleExpand,
  qrCodeFilter,
  qrCodeFilterOptions,
  totalPages,
}) {
  const [failedPhotos, setFailedPhotos] = useState({});

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: "16px",
        border: "2px solid #e0f2e9",
        backgroundColor: "#fcffff",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexDirection: { xs: "column", sm: "row" } }}>
        <TextField
          label="Buscar por nombre o codigo"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#FFA500" }} />
              </InputAdornment>
            ),
          }}
          onChange={onSearchChange}
        />
        <TextField
          select
          label="Filtrar por QR asignado"
          value={qrCodeFilter}
          onChange={onQrCodeFilterChange}
          sx={{
            minWidth: { xs: "100%", sm: 220 },
            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          {qrCodeFilterOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Typography
        variant="h6"
        sx={{
          mb: 1,
          color: "#0D0D0D ",
          fontFamily: "'Comic Relief'",
          fontWeight: "bold",
        }}
      >
        Lista de Mascotas
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <List>
        {currentPets.map((pet) => {
          const isExpanded = expandedPetId === pet.id;
          const hasAccessiblePhoto =
            isPetPhotoUrl(pet.photo_url) && !failedPhotos[pet.id];
          const assignedQr = getAssignedQr(pet);

          return (
            <ListItem
              key={pet.id}
              alignItems="flex-start"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
                borderBottom: "1px solid #e0f2e9",
                cursor: "pointer",
                px: 2,
                py: 1,
              }}
              onClick={() => onToggleExpand(pet.id)}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                  {hasAccessiblePhoto ? (
                    <Box
                      component="img"
                      src={pet.photo_url}
                      alt={pet.name}
                      onError={() =>
                        setFailedPhotos((prev) => ({ ...prev, [pet.id]: true }))
                      }
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        objectFit: "cover",
                        flexShrink: 0,
                        border: "2px solid #e0f2e9",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        flexShrink: 0,
                        bgcolor: "#e0f2e9",
                        color: "#1B8D4B",
                        fontWeight: 700,
                      }}
                    >
                      {pet.name?.charAt(0)?.toUpperCase() || "M"}
                    </Avatar>
                  )}
                  <ListItemText
                    primary={pet.name}
                    secondary={`Codigo: ${pet.pet_code}`}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    sx={{
                      border: "none",
                      backgroundColor: "transparent",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(pet);
                    }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(pet.id);
                    }}
                    sx={{
                      border: "none",
                      backgroundColor: "transparent",
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    Eliminar
                  </Button>
                </Box>
              </Box>

              {isExpanded && (
                <Box sx={{ mt: 1, display: "flex", gap: 2 }}>
                  {hasAccessiblePhoto && (
                    <Box
                      component="img"
                      src={pet.photo_url}
                      alt={pet.name}
                      onError={() =>
                        setFailedPhotos((prev) => ({ ...prev, [pet.id]: true }))
                      }
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Especie: {pet.species}
                      <br />
                      Contacto: {pet.contact_name}
                      <br />
                      Tel 1: {pet.primary_phone}
                      <br />
                      Tel 2: {pet.secondary_phone || "N/A"}
                      <br />
                      Zona: {pet.zone || "N/A"}
                      <br />
                      Estado: {pet.status}
                      <br />
                      QR:{" "}
                      {assignedQr
                        ? assignedQr.label ||
                          assignedQr.description ||
                          `QR #${assignedQr.id}`
                        : "Sin QRs"}
                      <br />
                      Notas: {pet.notes || "Sin notas"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </ListItem>
          );
        })}
      </List>

      {filteredPets.length > itemsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                color: "#1B8D4B",
                "&.Mui-selected": {
                  backgroundColor: "#1B8D4B",
                  color: "white",
                },
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
